import {
  SUPPORTED_LANGUAGES,
  canonicalLanguages,
  type LanguageCode,
} from "@/constants/languages";
import { API_ENDPOINTS } from "@/constants/api";

export const CATALOGUES_CACHE_KEY = "catalogues";
export const catalogueCacheKey = (id: string) => `catalogue:${id}`;

export const PROCESSING_FEATURE_IDS = [
  "imageSearch",
  "semanticTextIndexing",
  "aiProcessing",
  "moderation",
  "deduplication",
  "roomVisualiserDataExtraction",
] as const;

export type ProcessingFeatureId = (typeof PROCESSING_FEATURE_IDS)[number];
export type ProcessingFeatures = Record<ProcessingFeatureId, boolean>;
export type CatalogueProvisioningStatus = "provisioning" | "ready" | "failed";

export const ALL_PROCESSING_FEATURES: ProcessingFeatures = {
  imageSearch: true,
  semanticTextIndexing: true,
  aiProcessing: true,
  moderation: true,
  deduplication: true,
  roomVisualiserDataExtraction: false,
};

export const NO_PROCESSING_FEATURES: ProcessingFeatures = {
  imageSearch: false,
  semanticTextIndexing: false,
  aiProcessing: false,
  moderation: false,
  deduplication: false,
  roomVisualiserDataExtraction: false,
};

export interface Catalogue {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  enabled: boolean;
  deletedAt?: string;
  purgedAt?: string;
  languages: LanguageCode[];
  processingFeatures: ProcessingFeatures;
  capabilities: string[];
  provisioningStatus: CatalogueProvisioningStatus;
}

export interface CatalogueFeature {
  id: string;
  label: string;
  description: string;
  capabilities: string[];
  allowed: boolean;
}

export interface CatalogueCreateOptions {
  processingFeatures: ProcessingFeatures;
  languages: LanguageCode[];
  category_template_id?: string;
}

export interface RoomVisualiserFeatureState {
  catalogueId: string;
  processingFeatures: ProcessingFeatures;
  capabilities: string[];
}

type JsonRecord = Record<string, unknown>;
const supportedCodes = new Set<string>(SUPPORTED_LANGUAGES.map(({ code }) => code));

function isRecord(value: unknown): value is JsonRecord {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function isProcessingFeatureId(value: string): value is ProcessingFeatureId {
  return (PROCESSING_FEATURE_IDS as readonly string[]).includes(value);
}

function requiredString(value: unknown, field: string): string {
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(`Catalogue response has an invalid ${field}`);
  }
  return value;
}

function requiredBoolean(value: unknown, field: string): boolean {
  if (typeof value !== "boolean") {
    throw new Error(`Catalogue response has an invalid ${field}`);
  }
  return value;
}

function dateString(value: unknown, field: string): string {
  const date = typeof value === "string" ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) {
    throw new Error(`Catalogue response has an invalid ${field}`);
  }
  return date.toISOString();
}

function optionalDateString(value: unknown, field: string): string | undefined {
  return value === undefined || value === null ? undefined : dateString(value, field);
}

function parseLanguages(value: unknown): LanguageCode[] {
  if (
    !Array.isArray(value) ||
    value.length === 0 ||
    !value.every((language) => typeof language === "string" && supportedCodes.has(language))
  ) {
    throw new Error("Catalogue response has invalid languages");
  }
  return canonicalLanguages(value as LanguageCode[]);
}

function parseProcessingFeatures(value: unknown): ProcessingFeatures {
  if (!isRecord(value)) throw new Error("Catalogue response has invalid processing features");
  const features = {} as ProcessingFeatures;
  for (const id of PROCESSING_FEATURE_IDS) {
    if (id === "roomVisualiserDataExtraction" && value[id] === undefined) {
      features[id] = false;
      continue;
    }
    features[id] = requiredBoolean(value[id], `processingFeatures.${id}`);
  }
  return features;
}

function parseCapabilities(value: unknown): string[] {
  if (!Array.isArray(value) || !value.every((capability) => typeof capability === "string")) {
    throw new Error("Catalogue response has invalid capabilities");
  }
  return [...value];
}

function parseProvisioningStatus(value: unknown): CatalogueProvisioningStatus {
  if (value === undefined || value === null) return "ready";
  if (value === "provisioning" || value === "ready" || value === "failed") return value;
  return "failed";
}

function parseCatalogue(value: unknown): Catalogue {
  if (!isRecord(value)) throw new Error("Catalogue response has invalid data");
  return {
    id: requiredString(value.id, "id"),
    name: requiredString(value.name, "name"),
    ...(typeof value.description === "string" ? { description: value.description } : {}),
    enabled: requiredBoolean(value.enabled, "enabled"),
    createdAt: dateString(value.createdAt, "createdAt"),
    updatedAt: dateString(value.updatedAt, "updatedAt"),
    ...(optionalDateString(value.deletedAt, "deletedAt")
      ? { deletedAt: optionalDateString(value.deletedAt, "deletedAt") }
      : {}),
    ...(optionalDateString(value.purgedAt, "purgedAt")
      ? { purgedAt: optionalDateString(value.purgedAt, "purgedAt") }
      : {}),
    languages: parseLanguages(value.languages),
    processingFeatures: parseProcessingFeatures(value.processingFeatures),
    capabilities: parseCapabilities(value.capabilities),
    provisioningStatus: parseProvisioningStatus(value.provisioningStatus),
  };
}

function parseFeature(value: unknown): CatalogueFeature {
  if (!isRecord(value)) throw new Error("Catalogue feature response has invalid data");
  return {
    id: requiredString(value.id, "feature id"),
    label: requiredString(value.label, "feature label"),
    description: requiredString(value.description, "feature description"),
    capabilities: parseCapabilities(value.capabilities),
    allowed: requiredBoolean(value.allowed, "feature entitlement"),
  };
}

async function catalogueFetch(url: string, init?: RequestInit): Promise<JsonRecord> {
  const response = await fetch(url, {
    ...init,
    credentials: "include",
    headers: {
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...(init?.headers ?? {}),
    },
  });
  const payload: unknown = await response.json().catch(() => null);
  if (!isRecord(payload)) {
    throw new Error(response.ok ? "Catalogue response was invalid" : `Catalogue request failed (${response.status})`);
  }
  if (!response.ok) {
    throw new Error(typeof payload.error === "string" ? payload.error : `Catalogue request failed (${response.status})`);
  }
  if (payload.success !== true) {
    throw new Error(typeof payload.error === "string" ? payload.error : "Catalogue request failed");
  }
  return payload;
}

export const CatalogueService = {
  getAll: async (): Promise<Catalogue[]> => {
    const response = await catalogueFetch(API_ENDPOINTS.CATALOGUES.BASE);
    if (!Array.isArray(response.data)) throw new Error("Catalogue list response was invalid");
    return response.data.map(parseCatalogue);
  },

  getById: async (id: string): Promise<Catalogue> => {
    const response = await catalogueFetch(API_ENDPOINTS.CATALOGUES.BY_ID(id));
    return parseCatalogue(response.data);
  },

  getFeatures: async (): Promise<CatalogueFeature[]> => {
    const response = await catalogueFetch(API_ENDPOINTS.CATALOGUES.FEATURES);
    if (!isRecord(response.data) || !Array.isArray(response.data.features)) {
      throw new Error("Catalogue feature response was invalid");
    }
    return response.data.features.map(parseFeature);
  },

  create: async (
    name: string,
    description: string | undefined,
    options: CatalogueCreateOptions
  ): Promise<Catalogue> => {
    if (!Object.values(options.processingFeatures).some(Boolean)) {
      throw new Error("Select at least one processing feature");
    }
    const createCatalogueRequest = {
      name,
      description,
      processingFeatures: options.processingFeatures,
      languages: canonicalLanguages(options.languages),
      ...(options.processingFeatures.aiProcessing && options.category_template_id
        ? { category_template_id: options.category_template_id }
        : {}),
    };
    const response = await catalogueFetch(API_ENDPOINTS.CATALOGUES.BASE, {
      method: "POST",
      body: JSON.stringify(createCatalogueRequest),
    });
    return parseCatalogue(response.data);
  },

  delete: async (id: string): Promise<void> => {
    await catalogueFetch(API_ENDPOINTS.CATALOGUES.BY_ID(id), { method: "DELETE" });
  },

  toggleEnabled: async (id: string): Promise<void> => {
    await catalogueFetch(`${API_ENDPOINTS.CATALOGUES.BY_ID(id)}/toggle`, { method: "PUT" });
  },

  setRoomVisualiserExtraction: async (id: string, enabled: boolean): Promise<RoomVisualiserFeatureState> => {
    const response = await catalogueFetch(API_ENDPOINTS.CATALOGUES.ROOM_VISUALISER_EXTRACTION(id), {
      method: "PUT",
      body: JSON.stringify({ enabled }),
    });
    const data = response.data;
    if (!isRecord(data) || data.catalogueId !== id) {
      throw new Error("Extraction setting response was invalid. Reload the catalogue to check the saved setting.");
    }
    return {
      catalogueId: id,
      processingFeatures: parseProcessingFeatures(data.processingFeatures),
      capabilities: parseCapabilities(data.capabilities),
    };
  },

  restore: async (id: string): Promise<void> => {
    await catalogueFetch(`${API_ENDPOINTS.CATALOGUES.BY_ID(id)}/restore`, { method: "POST" });
  },
};
